import { z } from "zod";

import {
  hasUniqueValues,
  IsoTimestampSchema,
  ShortTextSchema,
  SortOrderSchema,
  UserTextSchema,
  UUIDSchema,
} from "./shared";

export const PersonalDuaStatusSchema = z.enum([
  "inbox",
  "active",
  "resolved",
  "circumstances_changed",
  "archived",
]);

export const FocusLevelSchema = z.enum(["focus", "normal", "occasional"]);

const UniqueUuidArraySchema = z
  .array(UUIDSchema)
  .max(1_000)
  .refine(hasUniqueValues, "Relationship IDs must be unique");

const ContextTagSchema = z
  .string()
  .trim()
  .min(1)
  .max(80)
  .regex(/^[\p{L}\p{N}][\p{L}\p{N}\s_-]*$/u, "Invalid context tag");

const PersonalDuaMutableShape = {
  title: ShortTextSchema.default(""),
  body: UserTextSchema.default(""),
  arabicText: UserTextSchema.optional(),
  translationText: UserTextSchema.optional(),
  transliterationText: UserTextSchema.optional(),
  status: PersonalDuaStatusSchema.default("inbox"),
  focusLevel: FocusLevelSchema.default("normal"),
  collectionIds: UniqueUuidArraySchema.default([]),
  tagIds: UniqueUuidArraySchema.default([]),
  personIds: UniqueUuidArraySchema.default([]),
  sessionIds: UniqueUuidArraySchema.default([]),
  contextTags: z
    .array(ContextTagSchema)
    .max(100)
    .refine(hasUniqueValues, "Context tags must be unique")
    .default([]),
  favorite: z.boolean().default(false),
  lastReviewedAt: IsoTimestampSchema.optional(),
  reviewCount: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).default(0),
  sourceLibraryEntryId: z.string().trim().min(1).max(160).optional(),
  privateNotes: UserTextSchema.optional(),
  sortOrder: SortOrderSchema.optional(),
} satisfies z.ZodRawShape;

function requireDuaText(
  value: {
    title: string;
    body: string;
    arabicText?: string | undefined;
    translationText?: string | undefined;
    transliterationText?: string | undefined;
  },
  context: z.RefinementCtx,
): void {
  const candidates = [
    value.title,
    value.body,
    value.arabicText,
    value.translationText,
    value.transliterationText,
  ];
  if (candidates.every((candidate) => (candidate ?? "").trim().length === 0)) {
    context.addIssue({
      code: "custom",
      message: "A du'a must contain text in at least one field.",
      path: ["body"],
    });
  }
}

export const PersonalDuaCreateSchema = z
  .object(PersonalDuaMutableShape)
  .strict()
  .superRefine(requireDuaText);

export const PersonalDuaSchema = z
  .object({
    id: UUIDSchema,
    ...PersonalDuaMutableShape,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
    deletedAt: IsoTimestampSchema.optional(),
  })
  .strict()
  .superRefine(requireDuaText);

export const PersonalDuaUpdateSchema = z
  .object({
    title: ShortTextSchema.optional(),
    body: UserTextSchema.optional(),
    arabicText: UserTextSchema.optional(),
    translationText: UserTextSchema.optional(),
    transliterationText: UserTextSchema.optional(),
    status: PersonalDuaStatusSchema.optional(),
    focusLevel: FocusLevelSchema.optional(),
    collectionIds: UniqueUuidArraySchema.optional(),
    tagIds: UniqueUuidArraySchema.optional(),
    personIds: UniqueUuidArraySchema.optional(),
    sessionIds: UniqueUuidArraySchema.optional(),
    contextTags: z.array(ContextTagSchema).max(100).refine(hasUniqueValues).optional(),
    favorite: z.boolean().optional(),
    lastReviewedAt: IsoTimestampSchema.optional(),
    reviewCount: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
    sourceLibraryEntryId: z.string().trim().min(1).max(160).optional(),
    privateNotes: UserTextSchema.optional(),
    sortOrder: SortOrderSchema.optional(),
  })
  .strict();

export type PersonalDuaStatus = z.infer<typeof PersonalDuaStatusSchema>;
export type FocusLevel = z.infer<typeof FocusLevelSchema>;
export type PersonalDua = z.output<typeof PersonalDuaSchema>;
export type CreatePersonalDuaInput = z.input<typeof PersonalDuaCreateSchema>;
export type UpdatePersonalDuaInput = z.input<typeof PersonalDuaUpdateSchema>;
